import { useEffect, useState, type ReactNode } from 'react';
import { useRouterState } from '@tanstack/react-router';
import styles from './RouteProgressBar.module.css';

/**
 * A thin top-of-viewport progress bar that appears the instant a navigation
 * starts (0ms — unlike the route pending spinner, which only shows after
 * `defaultPendingMs`). With loaders we keep the current page on screen during a
 * pending navigation, so this is the immediate "your click registered" signal;
 * it fades out when the router goes idle. Reduced-motion users still see the bar
 * (the global guard just removes the slide).
 *
 * SSR note: the router is `pending` while it resolves the initial route on the
 * server, so a naive `data-active={status === 'pending'}` bakes `data-active="true"`
 * into the SSR HTML; the client then hydrates to idle, React refuses to patch the
 * mismatched attribute ("won't be patched up"), and the bar is left stuck visible
 * forever after a hard refresh. The `mounted` gate forces the bar inactive during
 * SSR *and* the first client render (so they agree — no mismatch), then lets real
 * navigations drive it. A user-initiated navigation always happens post-mount, so
 * the 0ms behaviour is preserved.
 */
export function RouteProgressBar(): ReactNode {
  const isNavigating = useRouterState({ select: (s) => s.status === 'pending' });
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);
  return (
    <div
      className={styles.bar ?? ''}
      data-active={mounted && isNavigating}
      role="progressbar"
      aria-hidden="true"
    />
  );
}
